import { useEffect, useState, useCallback } from 'react';
import { getKeywords, createKeyword, updateKeyword, removeKeyword } from 'api/keywords';
import { filterKeywords } from 'containers/pages/keywordManagement/libs';
import { useNotificationProvider } from 'hooks';
import { Keyword, KeywordFormData } from 'containers/pages/keywordManagement/types';

function useKeywordManagement() {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [searchedKeywords, setSearchedKeywords] = useState<Keyword[]>([]);
  const [searchInput, setSearchInput] = useState('');

  const onToggleForm = () => {
    setIsOpenForm((prev) => !prev);
  };

  const onSearchInput = (input: string) => {
    setSearchInput(input);
    if (!input) {
      setSearchedKeywords(keywords);
    } else {
      const filtered = filterKeywords(input, keywords);
      setSearchedKeywords(filtered);
    }
  };

  const onResetSearch = () => {
    setSearchInput('');
    setSearchedKeywords(keywords);
  };

  const onRefreshKeywords = useCallback(async () => {
    try {
      const result = await getKeywords();
      const data: Keyword[] = (result?.Data || []).map((k: Keyword) => ({
        ...k,
        Instructions: k.Instructions || '',
      }));
      setKeywords(data);
      setSearchedKeywords(data);
      setSearchInput('');
    } catch (error) {
      console.error('[useKeywordManagement] Refresh failed:', error);
      notifyError('Failed to refresh keywords');
    }
  }, [notifyError]);

  const onSaveKeyword = useCallback(
    async (formData: KeywordFormData, isEdit: boolean) => {
      try {
        const payload = {
          ...formData,
          Title: formData.Title.toUpperCase(),
        };

        if (isEdit && formData.Id) {
          await updateKeyword({ Id: formData.Id, Title: payload.Title, Instructions: payload.Instructions });
          notifySuccess('Keyword updated successfully');
        } else {
          await createKeyword({ Title: payload.Title, Instructions: payload.Instructions });
          notifySuccess('Keyword created successfully');
          setIsOpenForm(false);
        }
        await onRefreshKeywords();
      } catch (error) {
        console.error('[useKeywordManagement] Save failed:', error);
        notifyError('Failed to save keyword. Please try again.');
      }
    },
    [notifySuccess, notifyError, onRefreshKeywords],
  );

  const onDeleteKeyword = useCallback(
    async (id: number) => {
      try {
        await removeKeyword(id);
        notifySuccess('Keyword deleted successfully');
        await onRefreshKeywords();
      } catch (error) {
        console.error('[useKeywordManagement] Delete failed:', error);
        notifyError('Failed to delete keyword. Please try again.');
      }
    },
    [notifySuccess, notifyError, onRefreshKeywords],
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await getKeywords();
        const data: Keyword[] = (result?.Data || []).map((k: Keyword) => ({
          ...k,
          Instructions: k.Instructions || '',
        }));
        setKeywords(data);
        setSearchedKeywords(data);
      } catch (error) {
        console.error('[useKeywordManagement] Initial load failed:', error);
        notifyError('Failed to load keywords');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [notifyError]);

  const state = {
    isLoading,
    isOpenForm,
    searchedKeywords,
    searchInput,
  };

  const handler = {
    onToggleForm,
    onSearchInput,
    onResetSearch,
    onSaveKeyword,
    onDeleteKeyword,
  };

  return { state, handler };
}

export default useKeywordManagement;
